/**
 * Canvas Toolbox (V2)
 *
 * Compact floating button group + modal for adding nodes to the canvas.
 * Shows exactly 13 user-facing production node types in 3 categories:
 * - Agents (9): Character Designer, Location Designer, Object Designer, Image Generator,
 *               Video Generator, Sound Expert, Video Editor, Script Writer, Storyboard Designer
 * - Media (3): Image Upload, Video Upload, Audio Upload
 * - Layout (1): Group
 */

import { useState, useMemo, useCallback, useRef, memo, type ChangeEvent } from 'react'
import { useReactFlow } from 'reactflow'
import { LayoutGrid, Search, Library, Download } from 'lucide-react'
import { nodeSchemas, createNode, type NodeDataRecord, type NodeSchema } from './nodeRegistry'
import Modal from '../ui/Modal'
import { ProjectConfigGear } from './ProjectConfigGear'
import { cn } from '../../lib/utils'
import { ReferenceSelector } from './nodes/shared/ReferenceSelector'
import { ClipDownloadModal } from './ClipDownloadModal'
import { useProjectId } from '../../contexts/ProjectIdContext'
import { authenticatedFetch, getConfiguredApiBase } from '@ponpon/api-client'
import { toast } from 'sonner'
import type { ReactFlowCanvasEdgeData, ReactFlowNodeData } from '../../hooks/useYCanvas'

interface ImportReferenceResponse {
  node_type: string
  node_data: NodeDataRecord
  assets_copied: number
}

/**
 * Upload node types that should open the OS file picker first instead of
 * creating an empty node. Maps frontend node type → media type + accept filter.
 */
const UPLOAD_NODE_TYPES: Record<string, { mediaType: 'image' | 'video' | 'audio'; accept: string }> = {
  image: { mediaType: 'image', accept: 'image/*' },
  video: { mediaType: 'video', accept: 'video/*' },
  audio: { mediaType: 'audio', accept: 'audio/*' },
}

interface CanvasToolboxProps {
  onAddNode: (node: ReturnType<typeof createNode>, explicitData?: NodeDataRecord) => void
  onUploadFile?: (file: File, type: 'image' | 'video' | 'audio') => void
}

/**
 * Explicit allowlist of node types shown in the toolbox, mapped to their display category.
 * Only these 13 types appear — everything else is excluded regardless of schema.
 */
export const ALLOWED_TYPES: Record<string, string> = {
  characterDesigner: 'Agents',
  locationDesigner: 'Agents',
  objectDesigner: 'Agents',
  imageGenerator: 'Agents',
  videoGenerator: 'Agents',
  soundExpert: 'Agents',
  videoEditor: 'Agents',
  scriptWriter: 'Agents',
  storyboardDesigner: 'Agents',
  image: 'Media',
  video: 'Media',
  audio: 'Media',
  nodeGroup: 'Layout',
}

/**
 * Display labels for categories.
 */
const CATEGORY_LABELS: Record<string, string> = {
  Agents: 'Agents',
  Media: 'Media',
  Layout: 'Layout',
}

export const CanvasToolbox = memo(function CanvasToolbox({ onAddNode, onUploadFile }: CanvasToolboxProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [isAssetManagerOpen, setIsAssetManagerOpen] = useState(false)
  const [isClipDownloadOpen, setIsClipDownloadOpen] = useState(false)
  const { getViewport, getNodes, getEdges, fitView } = useReactFlow<ReactFlowNodeData, ReactFlowCanvasEdgeData>()
  const toolboxRef = useRef<HTMLDivElement>(null)
  const projectId = useProjectId()

  // Hidden file input for upload-type nodes (file-picker-first pattern)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const pendingMediaTypeRef = useRef<'image' | 'video' | 'audio'>('image')

  // Group schemas by allowed types only (allowlist approach)
  const groupedNodes = useMemo(() => {
    const groups: Record<string, Array<{ type: string; schema: NodeSchema }>> = {}
    for (const [type, category] of Object.entries(ALLOWED_TYPES)) {
      const schema = nodeSchemas[type]
      if (!schema) continue
      if (!groups[category]) groups[category] = []
      groups[category].push({ type, schema })
    }
    return groups
  }, [])

  // Apply search filter
  const filteredGroups = useMemo(() => {
    if (!searchQuery.trim()) return groupedNodes
    const q = searchQuery.toLowerCase()
    const out: typeof groupedNodes = {}
    for (const [cat, nodes] of Object.entries(groupedNodes)) {
      const hits = nodes.filter(({ type, schema }) =>
        schema.label.toLowerCase().includes(q) ||
        (schema.description && schema.description.toLowerCase().includes(q)) ||
        type.toLowerCase().includes(q)
      )
      if (hits.length) out[cat] = hits
    }
    return out
  }, [groupedNodes, searchQuery])

  const handleAddNode = useCallback((type: string) => {
    // For upload node types, open the OS file picker first instead of creating an empty node
    const uploadInfo = UPLOAD_NODE_TYPES[type]
    if (uploadInfo && onUploadFile && fileInputRef.current) {
      pendingMediaTypeRef.current = uploadInfo.mediaType
      fileInputRef.current.accept = uploadInfo.accept
      fileInputRef.current.click()
      setIsOpen(false)
      return
    }

    const vp = getViewport()
    // Measure the actual canvas container width via closest .react-flow ancestor
    const rfContainer = toolboxRef.current?.closest('.react-flow')
    const cw = rfContainer ? rfContainer.getBoundingClientRect().width : window.innerWidth
    const cx = (-vp.x + cw / 2) / vp.zoom
    const cy = (-vp.y + window.innerHeight / 2) / vp.zoom

    // Collision avoidance: nudge position until free of existing nodes
    const OFFSET_STEP = 40
    let offsetX = 0
    let offsetY = 0
    const existingPositions = getNodes().map(n => n.position)
    for (let i = 0; i < 10; i++) {
      const testX = cx + offsetX
      const testY = cy + offsetY
      const tooClose = existingPositions.some(
        p => Math.abs(p.x - testX) < 50 && Math.abs(p.y - testY) < 50
      )
      if (!tooClose) break
      offsetX += OFFSET_STEP
      offsetY += OFFSET_STEP
    }

    const position = { x: cx + offsetX, y: cy + offsetY }
    const newNode = createNode(type, position)
    onAddNode(newNode, {})

    // Auto-focus on the newly created node
    requestAnimationFrame(() => {
      fitView({
        nodes: [{ id: newNode.id, position, data: {} }],
        padding: 0.3,
        duration: 500,
        maxZoom: vp.zoom,
      })
    })

    setIsOpen(false)
  }, [getViewport, getNodes, fitView, onAddNode, onUploadFile])

  /** Called when the user picks a file (or cancels — files will be empty). */
  const handleFileSelected = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      onUploadFile?.(file, pendingMediaTypeRef.current)
    }
    // Reset so the same file can be selected again
    e.target.value = ''
  }, [onUploadFile])

  const handleOpen = useCallback(() => {
    setSearchQuery('')
    setIsOpen(true)
  }, [])

  const handleDeleteFromLibrary = useCallback(async (nodeId: string) => {
    try {
      const resp = await authenticatedFetch(
        `${getConfiguredApiBase()}/library/nodes/${nodeId}`,
        { method: 'DELETE' }
      )
      if (!resp.ok) {
        const err = await resp.json().catch(() => null)
        throw new Error(err?.detail || resp.statusText)
      }
      toast.success('Removed from library')
    } catch (err) {
      toast.error(`Failed to delete: ${err instanceof Error ? err.message : 'Unknown error'}`)
    }
  }, [])

  const handleAddToCanvas = useCallback(
    async (references: { nodeId: string; nodeType: string; label: string; assetPath: string; mediaType: string }[]) => {
      if (references.length === 0) return

      // Close modal immediately for responsive feel
      setIsAssetManagerOpen(false)

      const toastId = toast.loading('Adding to canvas…')

      // Calculate viewport center for positioning
      const vp = getViewport()
      const rfContainer = toolboxRef.current?.closest('.react-flow')
      const cw = rfContainer ? rfContainer.getBoundingClientRect().width : window.innerWidth
      const baseCx = (-vp.x + cw / 2) / vp.zoom
      const baseCy = (-vp.y + window.innerHeight / 2) / vp.zoom

      const apiBase = getConfiguredApiBase()
      let added = 0

      for (let i = 0; i < references.length; i++) {
        const ref = references[i]
        try {
          // Call backend to copy assets and get full node data
          const resp = await authenticatedFetch(
            `${apiBase}/library/nodes/${ref.nodeId}/import`,
            {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ target_project_id: projectId }),
            }
          )

          if (!resp.ok) {
            const err = await resp.json().catch(() => null)
            console.error(`[AssetManager] Import failed for ${ref.nodeId}:`, err?.detail || resp.statusText)
            continue
          }

          const { node_type, node_data }: ImportReferenceResponse = await resp.json()

          // Offset each node to avoid stacking
          const position = {
            x: baseCx + i * 60,
            y: baseCy + i * 60,
          }

          onAddNode(createNode(node_type, position, node_data), node_data)
          added++
        } catch (error) {
          console.error(`[AssetManager] Import error for ${ref.nodeId}:`, error)
        }
      }

      if (added > 0) {
        toast.success(added === 1 ? 'Added to canvas' : `Added ${added} nodes to canvas`, { id: toastId })
      } else {
        toast.error('Failed to add to canvas', { id: toastId })
      }
    },
    [getViewport, onAddNode, projectId]
  )

  const hasResults = Object.keys(filteredGroups).length > 0

  return (
    <>
      {/* Hidden file input for upload-type nodes (file-picker-first) */}
      <input
        ref={fileInputRef}
        type="file"
        onChange={handleFileSelected}
        className="hidden"
        data-testid="toolbox-upload-file-input"
      />

      {/* Compact toolbox buttons */}
      <div ref={toolboxRef} className="flex flex-col items-center gap-1 p-1.5 bg-card border border-border rounded-md">
        <button
          onClick={handleOpen}
          className={cn(
            'w-9 h-9 rounded-lg flex items-center justify-center',
            'hover:bg-muted transition-colors cursor-pointer group'
          )}
          title="Add nodes"
          data-testid="toolbox-add-nodes"
        >
          <LayoutGrid className="w-4 h-4 text-muted-foreground group-hover:text-foreground transition-colors" />
        </button>

        <button
          onClick={() => setIsAssetManagerOpen(true)}
          className={cn(
            'w-9 h-9 rounded-lg flex items-center justify-center',
            'hover:bg-muted transition-colors cursor-pointer group'
          )}
          title="Asset Manager"
          data-testid="toolbox-asset-manager"
        >
          <Library className="w-4 h-4 text-muted-foreground group-hover:text-foreground transition-colors" />
        </button>

        <button
          onClick={() => setIsClipDownloadOpen(true)}
          className={cn(
            'w-9 h-9 rounded-lg flex items-center justify-center',
            'hover:bg-muted transition-colors cursor-pointer group'
          )}
          title="Download Assets"
          data-testid="toolbox-download-clips"
        >
          <Download className="w-4 h-4 text-muted-foreground group-hover:text-foreground transition-colors" />
        </button>

        <ProjectConfigGear />

      </div>

      {/* Add Node modal */}
      <Modal isOpen={isOpen} onClose={() => setIsOpen(false)} title="Add Node" size="md">
        <div className="space-y-3">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search nodes..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={cn(
                'w-full pl-9 pr-3 py-2 text-sm bg-muted border border-border rounded-lg',
                'text-foreground placeholder-muted-foreground',
                'focus:outline-none focus:border-primary/50 transition-colors'
              )}
              autoFocus
              data-testid="toolbox-search"
            />
          </div>

          {/* Node list */}
          <div className="max-h-72 overflow-y-auto -mx-1 px-1">
            {!hasResults && (
              <div className="py-8 text-center text-muted-foreground text-xs" data-testid="toolbox-empty-state">
                {searchQuery ? 'No matches' : 'No nodes available'}
              </div>
            )}

            {Object.entries(filteredGroups).map(([category, categoryNodes]) => (
              <div key={category} className="mb-2" data-testid={`toolbox-category-${category}`}>
                <div className="px-1 pt-2 pb-1.5">
                  <span className="text-xxs font-medium text-muted-foreground uppercase tracking-wider">
                    {CATEGORY_LABELS[category] ?? category}
                  </span>
                </div>
                <div className="grid grid-cols-3 gap-1">
                  {categoryNodes.map(({ type, schema }) => (
                    <button
                      key={type}
                      onClick={() => handleAddNode(type)}
                      className={cn(
                        'flex items-center gap-2 px-2.5 py-2 rounded-lg',
                        'hover:bg-muted cursor-pointer transition-colors group text-left'
                      )}
                      title={schema.description || `Add ${schema.label}`}
                      data-testid={`toolbox-node-${type}`}
                    >
                      <span className="text-sm shrink-0 opacity-60 group-hover:opacity-100 transition-opacity">
                        {schema.icon || '\u{1F4E6}'}
                      </span>
                      <span className="text-xs text-muted-foreground group-hover:text-foreground truncate transition-colors">
                        {schema.label}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </Modal>

      {/* Asset Manager modal */}
      <ReferenceSelector
        isOpen={isAssetManagerOpen}
        onClose={() => setIsAssetManagerOpen(false)}
        onSave={() => {}}
        excludeNodeId=""
        canvasNodes={getNodes()}
        mode="asset-manager"
        onAddToCanvas={handleAddToCanvas}
        onDeleteFromLibrary={handleDeleteFromLibrary}
        maxReferences={99}
      />

      {/* Clip Download modal */}
      <ClipDownloadModal
        isOpen={isClipDownloadOpen}
        onClose={() => setIsClipDownloadOpen(false)}
        canvasNodes={getNodes()}
        canvasEdges={getEdges()}
      />
    </>
  )
})
